import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import { inr, num, apiError } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import { useDataTable, TableToolbar, TablePager } from '../components/DataTable';

const UNITS = ['kg', 'g', 'l', 'ml', 'piece'];
const empty = { name: '', kind: 'ingredient', unit: 'kg', supplierId: '', purchasePrice: 0, minStock: 0, sku: '', isActive: true, itemCode: '', department: '', category: '', subCategory: '', brand: '', subBrand: '' };

export const MaterialModal = ({ material, suppliers, onClose, onSaved, onAddSupplier }) => {
  const [form, setForm] = useState(material ? { ...empty, ...material, supplierId: material.supplierId || '' } : empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const payload = { ...form, supplierId: form.supplierId || null, purchasePrice: Number(form.purchasePrice) || 0, minStock: Number(form.minStock) || 0 };
      const { data } = material ? await api.put(`/raw-materials/${material.id}`, payload) : await api.post('/raw-materials', payload);
      toast.success('Saved'); onSaved(data.material); onClose();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title={material ? 'Edit Raw Material' : 'Add Raw Material'} onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button></>}>
      <form onSubmit={save}>
        <div className="row">
          <div className="field" style={{ flex: 2 }}><label>Name *</label><input value={form.name} onChange={(e) => set('name', e.target.value)} required /></div>
          <div className="field"><label>Unit *</label><select value={form.unit} onChange={(e) => set('unit', e.target.value)}>{UNITS.map((u) => <option key={u}>{u}</option>)}</select></div>
        </div>
        <div className="row">
          <div className="field"><label>Purchase price / unit</label><input type="number" step="0.01" value={form.purchasePrice} onChange={(e) => set('purchasePrice', e.target.value)} /></div>
          <div className="field"><label>Min stock</label><input type="number" step="0.001" value={form.minStock} onChange={(e) => set('minStock', e.target.value)} /></div>
        </div>
        <div className="row">
          <div className="field"><label>Supplier</label>
            <div className="flex gap">
              <select value={form.supplierId} onChange={(e) => set('supplierId', e.target.value)}>
                <option value="">— none —</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <button type="button" className="btn btn-outline btn-sm" onClick={async () => { const s = await onAddSupplier(); if (s) set('supplierId', s.id); }}>+ New</button>
            </div>
          </div>
          <div className="field"><label>SKU</label><input value={form.sku || ''} onChange={(e) => set('sku', e.target.value)} /></div>
        </div>
        <div className="row">
          <div className="field"><label>Item code</label><input value={form.itemCode || ''} onChange={(e) => set('itemCode', e.target.value)} /></div>
          <div className="field"><label>Department</label><input value={form.department || ''} onChange={(e) => set('department', e.target.value)} /></div>
          <div className="field"><label>Category</label><input value={form.category || ''} onChange={(e) => set('category', e.target.value)} /></div>
        </div>
        <div className="row">
          <div className="field"><label>Sub category</label><input value={form.subCategory || ''} onChange={(e) => set('subCategory', e.target.value)} /></div>
          <div className="field"><label>Brand</label><input value={form.brand || ''} onChange={(e) => set('brand', e.target.value)} /></div>
          <div className="field"><label>Sub brand</label><input value={form.subBrand || ''} onChange={(e) => set('subBrand', e.target.value)} /></div>
        </div>
        {error && <p className="form-error">{error}</p>}
      </form>
    </Modal>
  );
};

// Goods-received (GRN) — stock in + moving-average cost. Reused by the detail page.
export const ReceiveModal = ({ material, onClose, onDone }) => {
  const [quantity, setQuantity] = useState('');
  const [unitCost, setUnitCost] = useState(material.purchasePrice || '');
  const [locationId, setLocationId] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Raw materials always live at the Central Kitchen — no location choice.
  useEffect(() => {
    api.get('/locations').then(({ data }) => {
      const kitchen = (data.locations || []).find((l) => l.type === 'kitchen');
      setLocationId(kitchen?.id || '');
    }).catch(() => {});
  }, []);

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await api.post(`/raw-materials/${material.id}/receive`, {
        quantity: Number(quantity), unitCost: Number(unitCost),
        locationId: locationId || undefined, expiryDate: expiryDate || undefined,
      });
      toast.success(`Received ${quantity} ${material.unit} — avg cost now ${inr(data.received.newAvgCost)}`);
      onDone && onDone(data.material); onClose();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title={`Receive stock — ${material.name}`} onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={submit} disabled={busy || !(Number(quantity) > 0)}>{busy ? 'Receiving…' : 'Receive'}</button></>}>
      <div className="row">
        <div className="field"><label>Quantity ({material.unit}) *</label><input type="number" step="0.001" value={quantity} onChange={(e) => setQuantity(e.target.value)} autoFocus /></div>
        <div className="field"><label>Unit cost (₹)</label><input type="number" step="0.01" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} /></div>
      </div>
      <div className="row">
        <div className="field"><label>Expiry (optional)</label><input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} /></div>
      </div>
      <p className="muted small">New on-hand cost is a moving average of current stock and this receipt.</p>
      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
};

// ── Bulk import from a stock-report CSV ────────────────────────
const HEADER_MAP = {
  id: 'id',
  itemcode: 'itemCode', code: 'itemCode',
  productname: 'name', name: 'name',
  departmentname: 'department', department: 'department',
  categoryname: 'category', category: 'category',
  subcategoryname: 'subCategory', subcategory: 'subCategory',
  brandname: 'brand', brand: 'brand',
  subbrandname: 'subBrand', subbrand: 'subBrand',
  unit: 'unit',
  purchaseprice: 'purchasePrice', cost: 'purchasePrice', price: 'purchasePrice',
  minstock: 'minStock', min: 'minStock',
  sku: 'sku',
  supplier: 'supplier', suppliername: 'supplier',
  status: 'isActive', isactive: 'isActive', active: 'isActive',
  totalavailableqty: 'quantity', availableqty: 'quantity', totalqty: 'quantity', qty: 'quantity', quantity: 'quantity',
  currentstock: 'currentStock', onhand: 'currentStock', stockvalue: 'stockValue',   // read-only, ignored on import
};

// Column order used by the "Download CSV" export; the import recognises all of these.
const EXPORT_COLS = [
  ['Id', 'id'], ['Item Code', 'itemCode'], ['Product Name', 'name'],
  ['Department Name', 'department'], ['Category Name', 'category'], ['Sub Category Name', 'subCategory'],
  ['Brand Name', 'brand'], ['Sub Brand Name', 'subBrand'], ['Unit', 'unit'],
  ['Purchase Price', 'purchasePrice'], ['Min Stock', 'minStock'], ['SKU', 'sku'],
  ['Supplier', 'supplier'], ['Status', 'status'], ['Current Stock', 'currentStock'],
];
const csvCell = (v) => {
  const s = v == null ? '' : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const exportMaterialsCSV = (materials) => {
  const header = EXPORT_COLS.map(([h]) => h).join(',');
  const lines = materials.map((m) => EXPORT_COLS.map(([, key]) => {
    if (key === 'supplier') return csvCell(m.supplier?.name || '');
    if (key === 'status') return csvCell(m.isActive ? 'Active' : 'Inactive');
    if (key === 'currentStock') return csvCell(m.currentStock ?? '');
    return csvCell(m[key] ?? '');
  }).join(','));
  const csv = [header, ...lines].join('\r\n') + '\r\n';
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });   // BOM so Excel keeps unicode
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const stamp = new Date().toISOString().slice(0, 10);
  a.href = url; a.download = `raw-materials-${stamp}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};
const normHeader = (h) => String(h || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Minimal RFC-4180-ish CSV parser (handles quotes, embedded commas/newlines).
function parseCSV(text) {
  const rows = []; let row = []; let field = ''; let i = 0; let inQuotes = false;
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') { if (text[i + 1] === '"') { field += '"'; i += 2; continue; } inQuotes = false; i++; continue; }
      field += c; i++; continue;
    }
    if (c === '"') { inQuotes = true; i++; continue; }
    if (c === ',') { row.push(field); field = ''; i++; continue; }
    if (c === '\r') { i++; continue; }
    if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; i++; continue; }
    field += c; i++;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows;
}

const ImportModal = ({ onClose, onDone }) => {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [setOpening, setSetOpening] = useState(true);
  const [setCurrent, setSetCurrent] = useState(false);
  const [locationId, setLocationId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Raw materials always live at the Central Kitchen.
  useEffect(() => {
    api.get('/locations').then(({ data }) => {
      const kitchen = (data.locations || []).find((l) => l.type === 'kitchen');
      setLocationId(kitchen?.id || '');
    }).catch(() => {});
  }, []);

  const onFile = (e) => {
    const f = e.target.files[0]; if (!f) return;
    setFileName(f.name); setError(''); setResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const grid = parseCSV(String(reader.result));
        if (grid.length < 2) { setError('File has no data rows.'); setRows([]); return; }
        const headers = grid[0].map((h) => HEADER_MAP[normHeader(h)] || null);
        if (!headers.includes('name')) { setError('Could not find a "Product Name" column in the header row.'); setRows([]); return; }
        const out = [];
        for (let r = 1; r < grid.length; r++) {
          const cells = grid[r];
          if (!cells.length || cells.every((c) => !String(c).trim())) continue;
          const obj = {};
          headers.forEach((field, idx) => { if (field && field !== 'stockValue') obj[field] = String(cells[idx] || '').trim(); });
          if (!obj.name) continue; // skips the totals row and blanks
          out.push(obj);
        }
        setRows(out);
        // A downloaded-then-edited export carries a "Current Stock" column: offer to
        // set stock from it (and don't treat it as legacy opening stock).
        const isExport = headers.includes('id') || headers.includes('currentStock');
        if (isExport) { setSetOpening(false); setSetCurrent(headers.includes('currentStock')); }
        if (!out.length) setError('No data rows with a Product Name were found.');
      } catch (err) { setError('Could not parse file: ' + err.message); setRows([]); }
    };
    reader.readAsText(f);
  };

  const submit = async () => {
    setBusy(true); setError(''); setResult(null);
    try {
      const { data } = await api.post('/raw-materials/import', {
        rows, setOpeningStock: setOpening, setCurrentStock: setCurrent,
        locationId: (setOpening || setCurrent) ? Number(locationId) : undefined,
      });
      setResult(data.result);
      toast.success(`Uploaded — ${data.result.created} new, ${data.result.updated} updated${data.result.adjusted ? `, ${data.result.adjusted} stock set` : ''}`);
      onDone && onDone();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const csv = [
      'Item Code,Product Name,Department Name,Category Name,Sub Category Name,Brand Name,Sub Brand Name,Unit,Total Available Qty',
      '667,AAMUDAM OIL,GROCERIES,GROCERY,,AVAKAAYA PICKLES HOUSE,,KGS,0.50',
      '523,COCONUT 1 PC,GROCERIES,GROCERY,,AVAKAAYA PICKLES HOUSE,,PCS,0',
      '615,SUGAR,GROCERIES,GROCERY,,AVAKAAYA PICKLES HOUSE,,KGS,118.59',
    ].join('\r\n') + '\r\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'raw-materials-template.csv';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Modal title="Upload raw materials" onClose={onClose} wide
      footer={<><button className="btn btn-outline" onClick={onClose}>Close</button><button className="btn btn-primary" onClick={submit} disabled={busy || !rows.length}>{busy ? 'Uploading…' : `Upload ${rows.length} rows`}</button></>}>
      <p className="muted small" style={{ marginTop: 0 }}>Upload a <strong>CSV</strong> — either a downloaded raw-materials sheet you edited, or a stock report. Recognised columns: Id, Item Code, Product Name, Department/Category/Sub Category/Brand/Sub Brand, Unit, Purchase Price, Min Stock, SKU, Supplier, Status, Total Available Qty. Rows are matched by <strong>Id → Item Code → Name</strong>; only the columns present are updated (blank columns never wipe data). Units like KGS/PCS map automatically.</p>
      <button type="button" className="btn btn-outline btn-sm" onClick={downloadTemplate} style={{ marginBottom: '.8rem' }}>⭳ Download CSV template</button>
      <div className="field"><label>CSV file</label><input type="file" accept=".csv,text/csv" onChange={onFile} /></div>
      {fileName && <p className="muted small">{fileName} — <strong>{rows.length}</strong> rows detected.</p>}
      <label className="flex gap" style={{ alignItems: 'center' }}><input type="checkbox" style={{ width: 'auto' }} checked={setCurrent} onChange={(e) => { setSetCurrent(e.target.checked); if (e.target.checked) setSetOpening(false); }} /> Set current stock from the “Current Stock” column <span className="muted small">(adjusts inventory up or down to match)</span></label>
      <label className="flex gap" style={{ alignItems: 'center' }}><input type="checkbox" style={{ width: 'auto' }} checked={setOpening} onChange={(e) => { setSetOpening(e.target.checked); if (e.target.checked) setSetCurrent(false); }} /> Set opening stock from “Total Available Qty” <span className="muted small">(only items currently at 0)</span></label>
      {(setOpening || setCurrent) && (
        <p className="muted small">Stock is applied at the <strong>Central Kitchen</strong>. {setCurrent ? 'On-hand there is adjusted to match each row’s Current Stock.' : 'Opening stock applied only to items currently at 0 there — safe to re-import.'}</p>
      )}
      {rows.length > 0 && (
        <div className="table-wrap" style={{ maxHeight: 220, overflow: 'auto' }}>
          <table className="grid">
            <thead><tr><th>Code</th><th>Name</th><th>Unit</th><th className="right">Price</th><th className="right">Min</th><th className="right">Stock</th></tr></thead>
            <tbody>
              {rows.slice(0, 8).map((r, i) => <tr key={i}><td>{r.itemCode}</td><td>{r.name}</td><td>{r.unit}</td><td className="right">{r.purchasePrice}</td><td className="right">{r.minStock}</td><td className="right">{r.currentStock ?? r.quantity}</td></tr>)}
              {rows.length > 8 && <tr><td colSpan={6} className="muted">…and {rows.length - 8} more</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {result && <p className="mt"><strong>Done.</strong> Created {result.created}, updated {result.updated}{result.adjusted ? `, stock set on ${result.adjusted}` : ''}{result.stocked ? `, opening-stocked ${result.stocked}` : ''}, skipped {result.skipped}{result.errors?.length ? `, ${result.errors.length} errors` : ''}.</p>}
      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
};

// ── Zero out all raw stock ─────────────────────────────────────
// Destructive and bulk, so it asks twice: a dry run showing exactly what will
// change, then the typed confirmation the API also insists on.
const ZERO_CONFIRM = 'ZERO ALL RAW STOCK';

const ZeroStockModal = ({ onClose, onDone }) => {
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/locations').then(({ data }) => setLocations(data.locations || [])).catch(() => {});
  }, []);

  // Re-preview whenever the scope changes, so the numbers never describe a
  // different location than the one that is about to be cleared.
  useEffect(() => {
    setPreview(null);
    setBusy(true);
    api.post('/raw-materials/zero-stock', { confirm: ZERO_CONFIRM, dryRun: true, locationId: locationId || undefined })
      .then(({ data }) => setPreview(data.summary))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setBusy(false));
  }, [locationId]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/raw-materials/zero-stock', {
        confirm: ZERO_CONFIRM,
        locationId: locationId || undefined,
        reason: reason.trim() || undefined,
      });
      toast.success(data.message);
      onDone();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const nothingToDo = preview && preview.rows === 0;
  const ready = typed.trim().toUpperCase() === ZERO_CONFIRM && !busy && preview && !nothingToDo;

  return (
    <Modal title="Set all raw material stock to zero" onClose={onClose} wide
      footer={<>
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-danger" onClick={submit} disabled={!ready}>
          {busy ? 'Working…' : 'Zero all stock'}
        </button>
      </>}>
      <p className="muted small">
        This writes every raw material&apos;s on-hand quantity to zero by posting a
        stock adjustment — including any that have gone negative, which are
        corrected back up to nothing. It does not delete materials, prices or
        history — the adjustment shows up in each material&apos;s stock ledger and
        can be reversed by receiving stock back in.
      </p>

      <div className="field">
        <label>Location</label>
        <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
          <option value="">All locations</option>
          {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>

      <div className="field">
        <label>Reason (saved on every adjustment)</label>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Physical stock count, Apr 2026" />
      </div>

      {busy && !preview && <div className="spinner" />}

      {preview && (
        nothingToDo
          ? <p className="muted small">Nothing to clear — no raw material is holding stock here.</p>
          : (
            <>
              <p className="small">
                <strong>{preview.materials}</strong> material(s) across{' '}
                <strong>{preview.locations}</strong> location(s) will be cleared
                ({preview.rows} stock row(s){preview.negativeRows > 0 ? `, ${preview.negativeRows} of them negative` : ''}).
              </p>
              <div className="table-wrap" style={{ maxHeight: 220, overflowY: 'auto' }}>
                <table className="grid">
                  <thead><tr><th>Material</th><th className="num">On hand</th></tr></thead>
                  <tbody>
                    {preview.items.slice(0, 100).map((it, i) => (
                      <tr key={`${it.locationId}-${it.itemId}-${it.batchId}-${i}`}>
                        <td>{it.name}</td>
                        <td className="num">{it.quantity} {it.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.items.length > 100 && (
                <p className="muted small">Showing the first 100 of {preview.items.length} rows.</p>
              )}
            </>
          )
      )}

      <div className="field">
        <label>Type <code>{ZERO_CONFIRM}</code> to enable the button</label>
        <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={ZERO_CONFIRM} />
      </div>
    </Modal>
  );
};

const RawMaterials = () => {
  const { can } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(undefined);
  const [receiving, setReceiving] = useState(null);
  const [importing, setImporting] = useState(false);
  const [zeroing, setZeroing] = useState(false);

  const [deletingId, setDeletingId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  // The server removes a material outright only when nothing points at it, and
  // deactivates it otherwise — so the confirmation promises neither.
  const remove = async (m) => {
    if (!window.confirm(`Delete ${m.name}?

If it has stock movements, batches or recipe lines it will be deactivated instead of removed, so the history it belongs to stays readable.`)) return;
    setDeletingId(m.id);
    try {
      const { data } = await api.delete(`/raw-materials/${m.id}?hard=1`);
      toast.success(data.message || (data.deleted ? 'Deleted' : 'Deactivated'));
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setDeletingId(null); }
  };

  const load = () => { setLoading(true); api.get(`/raw-materials${showInactive ? '?all=1' : ''}`).then(({ data }) => setMaterials(data.materials || [])).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false)); };
  useEffect(() => { api.get('/suppliers').then(({ data }) => setSuppliers(data.suppliers || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [showInactive]);   // eslint-disable-line react-hooks/exhaustive-deps

  const addSupplier = async () => {
    const name = window.prompt('New supplier name:');
    if (!name) return null;
    try { const { data } = await api.post('/suppliers', { name }); setSuppliers((p) => [...p, data.supplier]); return data.supplier; }
    catch (e) { toast.error(apiError(e)); return null; }
  };
  const onSaved = () => load();
  const totalValue = materials.reduce((n, m) => n + Number(m.stockValue || 0), 0);

  const dt = useDataTable(materials, {
    searchPlaceholder: 'Search name, item code, SKU…',
    searchKeys: ['name', 'itemCode', 'sku'],
    filters: [
      { key: 'kind', label: 'Type', value: (m) => m.kind },
      { key: 'category', label: 'Category', value: (m) => m.category },
      { key: 'unit', label: 'Unit', value: (m) => m.unit },
      { key: 'status', label: 'Status', value: (m) => (m.isActive ? 'Active' : 'Inactive') },
    ],
  });

  return (
    <div>
      <div className="section-head">
        <span className="muted small">{materials.length} materials · stock value {inr(totalValue)}</span>
      </div>
      <TableToolbar dt={dt} right={<>
        <label className="chk" title="Deactivated materials are hidden until you ask for them">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Incl. inactive
        </label>
        <button className="btn btn-outline" onClick={() => exportMaterialsCSV(materials)} disabled={!materials.length} title="Download all raw materials as CSV to edit and re-upload">⭳ Download CSV</button>
        {can('rawmaterials:write') && <button className="btn btn-outline" onClick={() => setImporting(true)}>⭱ Upload CSV</button>}
        {can('rawmaterials:write') && <button className="btn btn-outline" onClick={() => setZeroing(true)} title="Set the on-hand quantity of every raw material to zero">Zero stock</button>}
        {can('rawmaterials:write') && <button className="btn btn-primary" onClick={() => setEditing(null)}>+ Add Material</button>}
      </>} />
      {loading ? <div className="spinner" /> : (
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Code</th><th>Name</th><th>Unit</th><th className="right">Cost</th><th className="right">On hand</th><th className="right">Value</th><th className="right">Min</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {dt.rows.map((m) => (
                <tr key={m.id}>
                  <td className="mono muted">{m.itemCode || '—'}</td>
                  <td><Link to={`/raw-materials/${m.id}`} className="link-strong">{m.name}</Link></td>
                  <td>{m.unit}</td>
                  <td className="right">{inr(m.purchasePrice)}</td>
                  <td className="right">{num(m.currentStock)} {m.belowMin && <span className="badge badge-red">low</span>}</td>
                  <td className="right">{inr(m.stockValue)}</td>
                  <td className="right">{num(m.minStock)}</td>
                  <td>{m.isActive ? <span className="badge badge-green">Active</span> : <span className="badge badge-gray">Inactive</span>}</td>
                  <td>
                    <div className="flex gap">
                      {can('rawmaterials:write') && <button className="btn btn-outline btn-sm" onClick={() => setReceiving(m)}>Receive</button>}
                      {can('rawmaterials:write') && <button className="btn btn-outline btn-sm" onClick={() => setEditing(m)}>Edit</button>}
                      {can('rawmaterials:write') && (
                        <button
                          className="btn btn-danger btn-sm"
                          disabled={deletingId === m.id}
                          title={`Delete ${m.name}`}
                          onClick={() => remove(m)}
                        >
                          {deletingId === m.id ? '…' : 'Delete'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {dt.rows.length === 0 && <tr><td colSpan={9} className="empty">{dt.isFiltered ? 'No materials match your search.' : 'No raw materials yet.'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {!loading && <TablePager dt={dt} />}
      {editing !== undefined && <MaterialModal material={editing} suppliers={suppliers} onClose={() => setEditing(undefined)} onSaved={onSaved} onAddSupplier={addSupplier} />}
      {receiving && <ReceiveModal material={receiving} onClose={() => setReceiving(null)} onDone={load} />}
      {importing && <ImportModal onClose={() => setImporting(false)} onDone={load} />}
      {zeroing && <ZeroStockModal onClose={() => setZeroing(false)} onDone={load} />}
    </div>
  );
};

export default RawMaterials;
